import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalParticipant, useRoomContext } from "@livekit/components-react";
import type { ByteStreamReader } from "livekit-client";

import {
    MAX_MEETING_FILE_BYTES,
    MEETING_FILE_TOPIC,
    type MeetingFileEntry,
    type MeetingFileRejection,
    meetingFileRejection,
    progressPercent,
    safeFileName,
} from "../domain/meetingFile";

export type MeetingFilesChannel = {
    /** Sent and received files, in the order they appeared. */
    files: MeetingFileEntry[];
    /** Why the last file picked was not sent, or null. */
    rejection: MeetingFileRejection | null;
    send: (file: File) => void;
};

/**
 * Handing a file across the call.
 *
 * Files travel as LiveKit byte streams on the room's data channel, the one the
 * chat and the whiteboard already share, so there is no upload, no bucket and
 * nothing kept. A received file is held in memory as a blob and offered for
 * download; leaving the call releases every one of them.
 *
 * The receiver trusts nothing the other client says. The name and declared
 * size are checked on arrival and the bytes are counted as they come, since a
 * modified client can declare one size and send another. A refused stream is
 * still read to the end and thrown away: LiveKit keeps queueing the chunks of
 * a stream nobody reads, so ignoring it would hold the file in memory anyway.
 *
 * Every blob is typed `application/octet-stream` whatever the sender declared,
 * so the browser saves it instead of rendering it as a page.
 */
export function useMeetingFiles(): MeetingFilesChannel {
    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();

    const [files, setFiles] = useState<MeetingFileEntry[]>([]);
    const [rejection, setRejection] = useState<MeetingFileRejection | null>(null);
    /** Object URLs this call handed out, released when it ends. */
    const urls = useRef(new Set<string>());

    const add = useCallback((entry: MeetingFileEntry) => {
        setFiles((current) => [...current, entry]);
    }, []);

    const update = useCallback((id: string, patch: Partial<MeetingFileEntry>) => {
        setFiles((current) =>
            current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
        );
    }, []);

    useEffect(() => {
        let disposed = false;
        const held = urls.current;

        const receive = async (reader: ByteStreamReader, identity: string) => {
            const { info } = reader;
            const name = safeFileName(info.name);
            let refused = meetingFileRejection({ name, size: info.size }) !== null;

            add({
                id: info.id,
                timestamp: info.timestamp,
                name,
                size: info.size,
                authorId: identity,
                authorName: room.remoteParticipants.get(identity)?.name ?? "",
                mine: false,
                status: refused ? "failed" : "transferring",
                progress: 0,
            });

            let shown = 0;
            reader.onProgress = (progress) => {
                const percent = progressPercent(progress);
                if (refused || percent === shown) return;
                shown = percent;
                update(info.id, { progress: percent });
            };

            const chunks: Uint8Array[] = [];
            let received = 0;
            try {
                for await (const chunk of reader) {
                    received += chunk.byteLength;
                    if (refused) continue;
                    if (received > MAX_MEETING_FILE_BYTES) {
                        refused = true;
                        chunks.length = 0;
                        update(info.id, { status: "failed" });
                        continue;
                    }
                    chunks.push(chunk);
                }
            } catch {
                if (!refused) update(info.id, { status: "failed" });
                return;
            }

            if (refused || disposed) return;
            const url = URL.createObjectURL(
                new Blob(chunks as BlobPart[], { type: "application/octet-stream" })
            );
            held.add(url);
            update(info.id, { status: "done", progress: 100, url });
        };

        room.registerByteStreamHandler(MEETING_FILE_TOPIC, (reader, participant) => {
            void receive(reader, participant.identity);
        });

        return () => {
            disposed = true;
            room.unregisterByteStreamHandler(MEETING_FILE_TOPIC);
            held.forEach((url) => URL.revokeObjectURL(url));
            held.clear();
        };
    }, [room, add, update]);

    const send = useCallback(
        (file: File) => {
            const name = safeFileName(file.name);
            const refused = meetingFileRejection({ name, size: file.size });
            setRejection(refused);
            if (refused) return;

            const id = `local-${crypto.randomUUID()}`;
            add({
                id,
                timestamp: Date.now(),
                name,
                size: file.size,
                authorId: localParticipant.identity,
                authorName: localParticipant.name ?? "",
                mine: true,
                status: "transferring",
                progress: 0,
            });

            let shown = 0;
            localParticipant
                .sendFile(file, {
                    topic: MEETING_FILE_TOPIC,
                    onProgress: (progress) => {
                        const percent = progressPercent(progress);
                        if (percent === shown) return;
                        shown = percent;
                        update(id, { progress: percent });
                    },
                })
                .then(() => update(id, { status: "done", progress: 100 }))
                .catch(() => update(id, { status: "failed" }));
        },
        [localParticipant, add, update]
    );

    return { files, rejection, send };
}
